// Country registry — single source of truth for all per-country static
// metadata (locale, currency symbol, phone format, account-number length,
// Paystack slug). The DB `Country` table controls activation (enabled /
// disabled by admin); this registry controls how each country is rendered
// and validated on the client.
//
// Adding a new country end-to-end:
//   1. Add an entry to COUNTRIES below.
//   2. Seed the matching `Country` row in the DB with isActive=true.
//   3. Configure RateConfig + per-tier TierLimitConfig for it via /admin.
//   4. Done — the new country surfaces in the sell-page toggle, the bot
//      country picker, the home-page hero, the service-health monitor,
//      bank lookups, and locale formatting. Zero additional code.
//
// If you find yourself adding a country-specific branch in app code rather
// than extending this file, the abstraction has leaked — fix it.

export type CountryConfig = {
  /** ISO-3166 alpha-2 — used everywhere as the canonical country key. */
  code: string;
  /** Display name. */
  name: string;
  /** ISO-4217 currency code. */
  currency: string;
  /** Unicode currency symbol shown in UI (₦, ₵, KSh, R, …). */
  currencySymbol: string;
  /** BCP-47 locale tag — used for Intl.NumberFormat / toLocaleString calls. */
  locale: string;
  /** Country flag emoji for compact UI affordances (toggles, pills). */
  flagEmoji: string;
  /** E.164 country dialing prefix, including the leading '+'. */
  phonePrefix: string;
  /** Example local-format phone (digits after the prefix) — used in placeholders. */
  phoneExampleLocal: string;
  /** Bank account number length the resolve API expects. */
  accountNumberLength: number;
  /** Paystack `country=` query parameter slug for /bank lookups. */
  paystackCountrySlug: string;
};

// THE registry. One entry per country we ship.
export const COUNTRIES: Record<string, CountryConfig> = {
  NG: {
    code: "NG",
    name: "Nigeria",
    currency: "NGN",
    currencySymbol: "₦",
    locale: "en-NG",
    flagEmoji: "🇳🇬",
    phonePrefix: "+234",
    phoneExampleLocal: "8012345678",
    accountNumberLength: 10,
    paystackCountrySlug: "nigeria",
  },
  GH: {
    code: "GH",
    name: "Ghana",
    currency: "GHS",
    currencySymbol: "₵",
    locale: "en-GH",
    flagEmoji: "🇬🇭",
    phonePrefix: "+233",
    phoneExampleLocal: "201234567",
    accountNumberLength: 13,
    paystackCountrySlug: "ghana",
  },
  KE: {
    code: "KE",
    name: "Kenya",
    currency: "KES",
    currencySymbol: "KSh",
    locale: "en-KE",
    flagEmoji: "🇰🇪",
    phonePrefix: "+254",
    phoneExampleLocal: "701234567",
    accountNumberLength: 10,
    paystackCountrySlug: "kenya",
  },
  ZA: {
    code: "ZA",
    name: "South Africa",
    currency: "ZAR",
    currencySymbol: "R",
    locale: "en-ZA",
    flagEmoji: "🇿🇦",
    phonePrefix: "+27",
    phoneExampleLocal: "701234567",
    accountNumberLength: 10,
    paystackCountrySlug: "south-africa",
  },
};

/** Look up by country code. Throws on unknown code so callers fail loud. */
export function countryFor(code: string): CountryConfig {
  const c = COUNTRIES[code.toUpperCase()];
  if (!c) throw new Error(`Unknown country code: ${code}`);
  return c;
}

/** Look up by currency code. Returns undefined for unknown currencies — some
 *  callers (locale formatters) gracefully degrade rather than throw. */
export function countryByCurrency(currency: string): CountryConfig | undefined {
  const upper = currency.toUpperCase();
  return Object.values(COUNTRIES).find((c) => c.currency === upper);
}

/** Every country in the registry. Server endpoints intersect this with the
 *  DB `Country.isActive` flag to compute the actually-bookable set. */
export function allRegisteredCountries(): CountryConfig[] {
  return Object.values(COUNTRIES);
}

/** Type guard for runtime validation of user input against the registry. */
export function isValidCountryCode(code: unknown): code is string {
  return typeof code === "string" && Object.prototype.hasOwnProperty.call(COUNTRIES, code.toUpperCase());
}
